/**
 * Mobile portrait rotates the whole app: `App.vue` puts `rotation90` /
 * `rotation-90` on `<body>` and `style.css` CSS-rotates it ±90°, so the app's
 * logical axes stop lining up with the screen's. Pointer events always report
 * *screen* coordinates, so anything relating them to the app's own layout —
 * travel along an axis, an offset into an element, a hit-test against layout
 * bounds — must project through here first, or a horizontal gesture responds to
 * vertical movement and vice versa.
 *
 * Math that is screen-space on *both* sides needs no projection and shouldn't get
 * one: a point-in-rect test against a live `getBoundingClientRect()`, a pinch
 * distance, a "moved at all" threshold. Rotation preserves those.
 *
 * The class names invert what they do: `body.rotation90` is `rotate(-90deg)`,
 * where logical +X runs screen bottom→top; `body.rotation-90` is
 * `rotate(90deg)`, where logical +X runs screen top→bottom.
 *
 * The rotation's translation is dropped, so a projected coordinate only means
 * something next to another one from the same space: difference two points, or
 * measure a point against `toLogicalRect` of the element it fell on.
 */

type BodyRotation = 'rotation90' | 'rotation-90' | 'none';

const bodyRotation = (): BodyRotation => {
  const { classList } = document.body;
  if (classList.contains('rotation90')) return 'rotation90';
  if (classList.contains('rotation-90')) return 'rotation-90';
  return 'none';
};

export type LogicalPoint = { x: number; y: number };

export function toLogicalPoint(clientX: number, clientY: number): LogicalPoint {
  switch (bodyRotation()) {
    case 'rotation90':
      return { x: -clientY, y: clientX };
    case 'rotation-90':
      return { x: clientY, y: -clientX };
    default:
      return { x: clientX, y: clientY };
  }
}

/** `toLogicalPoint` for the coordinates carried by a mouse, pointer or touch event. */
export function toLogicalEventPoint(event: MouseEvent | TouchEvent): LogicalPoint {
  const source = 'touches' in event ? event.touches[0] : event;
  return toLogicalPoint(source.clientX, source.clientY);
}

/**
 * An element's rect in the same projected space, so `left`/`top` anchor a
 * `toLogicalPoint` and `width` measures along logical X.
 */
export function toLogicalRect(rect: DOMRect) {
  switch (bodyRotation()) {
    case 'rotation90':
      return { left: -rect.bottom, top: rect.left, width: rect.height, height: rect.width };
    case 'rotation-90':
      return { left: rect.top, top: -rect.right, width: rect.height, height: rect.width };
    default:
      return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  }
}
